import { defineStore } from 'pinia'
import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface PartnerDetail {
  id: number
  company_name: string
  first_name: string
  last_name: string
  email: string
  street: string
  city: string
  zip_code: number
  country_id: number
  vat_number?: string | null
  slug?: string
  logo_url?: string | null
  logo_media_id?: number | null
}

export interface PartnersResponse {
  error: boolean
  message: string
  data: PartnerDetail[]
}

export interface CreatePartnerPayload {
  company_name: string
  first_name: string
  last_name: string
  email: string
  password: string
  street: string
  city: string
  zip_code: string
  country_id: number
  vat_number?: string | null
  slug?: string
}

export interface ValidationError {
  [key: string]: string[]
}

export const usePartnersListStore = defineStore('partnersList', () => {
  const partners = ref<PartnerDetail[]>([])
  const isLoading = ref(false)
  const error = ref('')
  const successMessage = ref('')

  const fetchPartners = async () => {
    isLoading.value = true
    error.value = ''

    try {
      const response = await apiInstance.get('/partners')
      const data = response.data

      if (response.status !== 200 || (data && data.error === true)) {
        error.value = data?.message || 'Failed to fetch partners'
        isLoading.value = false
        return
      }

      if (data?.data && Array.isArray(data.data)) {
        partners.value = data.data
      }

    } catch (err: any) {
      console.error('Error fetching partners:', err)
      error.value = err.response?.data?.message || 'Network error while fetching partners'
    } finally {
      isLoading.value = false
    }
  }

  const createPartner = async (payload: CreatePartnerPayload) => {
    try {
      const response = await apiInstance.post('/partners', payload)
      const data = response.data

      if (response.status < 200 || response.status >= 300 || (data && data.error === true)) {
        // Handle validation errors from server (422 status)
        if (response.status === 422 && data?.errors) {
          const error: any = new Error(data?.message || 'Validation error')
          error.validationErrors = data.errors as ValidationError
          throw error
        }
        console.error('API error:', data?.message)
        throw new Error(data?.message || 'Failed to create partner')
      }

      return {
        success: true,
        message: (data && data.message) || 'Partner created successfully',
        data: data?.data,
      }
    } catch (err: any) {
      console.error('Error creating partner:', err)

      // Handle axios error responses (including 422 validation errors)
      if (err.response) {
        const status = err.response.status
        const data = err.response.data

        // Handle validation errors from server (422 status)
        if (status === 422 && data?.errors) {
          const error: any = new Error(data?.message || 'Validation error')
          error.validationErrors = data.errors as ValidationError
          throw error
        }

        // Handle other API errors
        throw new Error(data?.message || err.message || 'Network error')
      }

      // Re-throw if already has validation errors attached
      if (err.validationErrors) {
        throw err
      }

      throw err
    }
  }

  const getPartnerById = async (partnerId: number): Promise<PartnerDetail> => {
    try {
      const response = await apiInstance.get(`/partners/${partnerId}`)
      const data = response.data

      if (response.status !== 200 || (data && data.error === true)) {
        throw new Error(data?.message || 'Failed to fetch partner details')
      }

      return data.data
    } catch (err: any) {
      console.error('Error fetching partner:', err)
      throw new Error(err.response?.data?.message || err.message || 'Network error')
    }
  }

  const editPartner = async (partnerId: number, payload: CreatePartnerPayload) => {
    try {
      const response = await apiInstance.put(`/partners/${partnerId}`, payload)
      const data = response.data

      if (response.status < 200 || response.status >= 300 || (data && data.error === true)) {
        // Handle validation errors from server (422 status)
        if (response.status === 422 && data?.errors) {
          const error: any = new Error(data?.message || 'Validation error')
          error.validationErrors = data.errors as ValidationError
          throw error
        }
        console.error('API error:', data?.message)
        throw new Error(data?.message || 'Failed to edit partner')
      }

      return {
        success: true,
        message: (data && data.message) || 'Partner updated successfully',
        data: data?.data,
      }
    } catch (err: any) {
      console.error('Error editing partner:', err)

      // Handle axios error responses (including 422 validation errors)
      if (err.response) {
        const status = err.response.status
        const data = err.response.data

        // Handle validation errors from server (422 status)
        if (status === 422 && data?.errors) {
          const error: any = new Error(data?.message || 'Validation error')
          error.validationErrors = data.errors as ValidationError
          throw error
        }

        // Handle other API errors
        throw new Error(data?.message || err.message || 'Network error')
      }

      // Re-throw if already has validation errors attached
      if (err.validationErrors) {
        throw err
      }

      throw err
    }
  }

  const uploadPartnerLogo = async (partnerId: number, file: File) => {
    const formData = new FormData()
    formData.append('logo', file)
    try {
      const response = await apiInstance.post(`/partners/${partnerId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const data = response.data
      if (data?.error) throw new Error(data.message || 'Failed to upload logo')
      return { data: { url: data?.data?.url ?? '', mediaId: data?.data?.id ?? null } }
    } catch (err: any) {
      const responseData = err.response?.data
      const validationMessage = responseData?.errors
        ? Object.values(responseData.errors as Record<string, string[]>).flat()[0]
        : null
      throw new Error(validationMessage || responseData?.message || err.message || 'Failed to upload logo')
    }
  }

  const deletePartnerLogo = async (partnerId: number, mediaId: number) => {
    try {
      const response = await apiInstance.delete(`/partners/${partnerId}/logo/${mediaId}`)
      const data = response.data
      if (data?.error) throw new Error(data.message || 'Failed to delete logo')
      return data
    } catch (err: any) {
      const responseData = err.response?.data
      const validationMessage = responseData?.errors
        ? Object.values(responseData.errors as Record<string, string[]>).flat()[0]
        : null
      throw new Error(validationMessage || responseData?.message || err.message || 'Failed to delete logo')
    }
  }

  const deletePartner = async (partnerId: number) => {
    try {
      const response = await apiInstance.delete(`/partners/${partnerId}`)
      const data = response.data

      if (response.status < 200 || response.status >= 300 || (data && data.error === true)) {
        console.error('API error:', data?.message)
        throw new Error(data?.message || 'Failed to delete partner')
      }

      return {
        success: true,
        message: (data && data.message) || 'Partner deleted successfully',
      }
    } catch (err: any) {
      console.error('Error deleting partner:', err)
      throw new Error(err.response?.data?.message || err.message || 'Network error')
    }
  }

  const reset = () => {
    partners.value = []
    isLoading.value = false
    error.value = ''
    successMessage.value = ''
  }

  return {
    partners,
    isLoading,
    error,
    successMessage,
    fetchPartners,
    getPartnerById,
    createPartner,
    editPartner,
    deletePartner,
    uploadPartnerLogo,
    deletePartnerLogo,
    reset,
  }
})
